/**
 * Per-route form configuration.
 *
 * Adding a new route:
 *   1. Add an entry to FORM_CONFIGS below.
 *   2. In App.tsx, render the page with the matching `configKey` prop.
 *
 * IMPORTANT: form IDs here mirror the live values used by each route today.
 * Do NOT change them without a product/marketing review — they drive the
 * downstream funnel, segmentation, and reporting.
 */

export type KnowCoding = "yes" | "no" | "";

/** Resolves a Beehiiv/api-forms form ID for a given user state. */
export type FormIdPicker = (args: {
  knowCoding: KnowCoding;
  countryCode: string;
}) => number;

/** Lead-tracking calls fired on validated submit. Each form picks the calls it currently makes. */
export type LeadTracking = {
  /** Wrapper that fires fbq + gtag/gtm. Used by `lead-form` today. */
  trackMarketingLead?: boolean;
  /** Bare `window.fbq?.("track", "Lead")`. Used by every other form today. */
  fbqLead?: boolean;
  /** GA4 event name to fire on API 200, before redirect. */
  gtagEvent?: string;
  /** X (Twitter) conversion event on API 200, before redirect. */
  twqLead?: boolean;
};

/** Extra UI fields rendered above the standard fields. Only `b2b` uses these today. */
export type ExtraField =
  | "designation"
  | "companyName"
  | "teamSize"
  | "programInterest";

/**
 * Hidden defaults for `questionResponses` — used when a step is skipped in the
 * UI but the API still requires that question (e.g. b2b sends a default
 * `professional_label` and `work_experience_range`).
 *
 * Keys are API question `label`s; values are the option title to send.
 */
export type HiddenDefaults = Record<string, string>;

export type FormConfig = {
  /** Stable key — also used as React key and analytics tag. */
  key: string;
  /** Returns the form ID for the current user state. Keep logic identical to the legacy file. */
  pickFormId: FormIdPicker;
  /** Which lead-tracking calls to fire on submit. Mirrors what each legacy form does today. */
  tracking: LeadTracking;
  /** Whether the user is asked "Do you know coding?". */
  askKnowCoding: boolean;
  /**
   * Render extra questions from the API question flow (unconditional + follow-ups).
   * Legacy vk/vaibhav forms only used the API for experience/years — not extra UI fields.
   * @default true
   */
  showApiExtraQuestions?: boolean;
  /**
   * Show the "India (detected)" country row. When false, geo still sets
   * countryCode for form IDs; users can change region via the phone dial picker.
   */
  showCountryField?: boolean;
  /**
   * Whether the user is asked their experience level (Student / Working Professional / …).
   * When false, `hiddenDefaults.professional_label` is sent silently.
   */
  askExperience: boolean;
  /**
   * Whether the user is asked years of experience.
   * When false, `hiddenDefaults.work_experience_range` is sent silently.
   */
  askYearOfExperience: boolean;
  /** Extra UI fields shown above the standard ones (b2b-only today). */
  extraFields?: ExtraField[];
  /** API-question defaults sent silently for any step that's hidden. */
  hiddenDefaults?: HiddenDefaults;
};

/**
 * Per-route configs. Each entry mirrors a legacy form component 1:1.
 *
 *   default     ← src/components/lead-form.tsx     (used by /, /ig, /x, /linkedin, /yt)
 *   community   ← src/components/community-form.tsx (/community)
 *   aiRoadmap   ← src/components/lead-from-2.tsx   (/ai-roadmap)
 *   vk          ← src/components/vk-form.tsx        (/vk)
 *   codex       ← src/components/codex.tsx          (/codex)
 *   vaibhav     ← src/components/form-vaibhav.tsx   (no route today — kept for future use)
 *   b2b         ← src/components/b2b-form.tsx       (/b2b)
 */
export const FORM_CONFIGS = {
  default: {
    key: "default",
    pickFormId: ({ knowCoding, countryCode }) =>
      knowCoding === "yes"
        ? countryCode === "IN"
          ? 18899
          : 18900
        : countryCode === "IN"
          ? 18707
          : 18708,
    tracking: { trackMarketingLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  community: {
    key: "community",
    pickFormId: ({ knowCoding, countryCode }) =>
      knowCoding === "yes"
        ? countryCode === "IN"
          ? 19131
          : 19130
        : countryCode === "IN"
          ? 19064
          : 19065,
    tracking: { fbqLead: true, gtagEvent: "staying_ahead_click", twqLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  aiRoadmap: {
    key: "aiRoadmap",
    pickFormId: ({ knowCoding, countryCode }) =>
      knowCoding === "yes"
        ? countryCode === "IN"
          ? 19131
          : 19130
        : countryCode === "IN"
          ? 18707
          : 18708,
    tracking: {},
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  vk: {
    key: "vk",
    pickFormId: ({ countryCode }) => (countryCode === "IN" ? 19812 : 19813),
    tracking: { fbqLead: true },
    askKnowCoding: false,
    showApiExtraQuestions: false,
    askExperience: true,
    askYearOfExperience: true,
  },

  codex: {
    key: "codex",
    pickFormId: ({ knowCoding }) => (knowCoding === "yes" ? 19819 : 19818),
    tracking: { fbqLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  vaibhav: {
    key: "vaibhav",
    pickFormId: ({ countryCode }) => (countryCode === "IN" ? 19064 : 19065),
    tracking: { fbqLead: true },
    askKnowCoding: false,
    showApiExtraQuestions: false,
    askExperience: true,
    askYearOfExperience: true,
  },

  b2b: {
    key: "b2b",
    pickFormId: () => 19592,
    tracking: { fbqLead: true },
    askKnowCoding: false,
    askExperience: false,
    askYearOfExperience: false,
    extraFields: ["designation", "companyName", "teamSize", "programInterest"],
    hiddenDefaults: {
      professional_label: "Salaried Professional",
      work_experience_range: "1-2 years",
    },
  },

  /** Used by /yt-v2 — modal flow with learning-path card step. */
  ytV2: {
    key: "ytV2",
    pickFormId: ({ knowCoding, countryCode }) =>
      knowCoding === "yes"
        ? countryCode === "IN"
          ? 20880
          : 20879
        : countryCode === "IN"
          ? 20881
          : 20882,
    tracking: { trackMarketingLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  rajPerf: {
    key: "rajPerf",
    pickFormId: ({ knowCoding, countryCode }) =>
      countryCode === "IN"
        ? knowCoding === "yes"
          ? 21705
          : 21704
        : 21770,
    tracking: { fbqLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },

  mastermind: {
    key: "mastermind",
    pickFormId: ({ countryCode }) => (countryCode === "IN" ? 21407 : 21408),
    tracking: { trackMarketingLead: true },
    askKnowCoding: false,
    askExperience: true,
    askYearOfExperience: true,
  },

  faye: {
    key: "faye",
    pickFormId: ({ countryCode }) => (countryCode === "IN" ? 24222 : 24223),
    tracking: { fbqLead: true },
    askKnowCoding: false,
    askExperience: true,
    askYearOfExperience: true,
  },

  /**
   * Used by the lead modal opened from /resources and /all-resources.
   * Same modal flow as the homepage; only the downstream form IDs differ.
   */
  resources: {
    key: "resources",
    pickFormId: ({ knowCoding, countryCode }) =>
      knowCoding === "yes"
        ? countryCode === "IN"
          ? 20318
          : 20319
        : countryCode === "IN"
          ? 20320
          : 20321,
    tracking: { trackMarketingLead: true },
    askKnowCoding: true,
    askExperience: true,
    askYearOfExperience: true,
  },
} as const satisfies Record<string, FormConfig>;

export type FormConfigKey = keyof typeof FORM_CONFIGS;

export function getFormConfig(key: FormConfigKey): FormConfig {
  return FORM_CONFIGS[key];
}
